const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatFullDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** "June 2019" or "Jun 12 – 18, 2019" when the range spans days. */
export function formatDateRange(startIso: string | null, endIso: string | null): string {
  if (!startIso) return '';
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return '';
  if (!endIso) return formatDate(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime()) || sameDay(start, end)) return formatDate(startIso);

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    const short = MONTHS[start.getMonth()].slice(0, 3);
    return `${short} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${formatDate(startIso)} – ${formatDate(endIso)}`;
}

/**
 * Full-precision range, always including the day number.
 *
 * `formatDateRange` collapses single-day spans to "June 2019", which is right for
 * a season header but wrong on an episode card, where the location line already
 * sits above it and "June 2019" would repeat information the title carries. The
 * card needs the day: "June 14, 2019" / "June 14–16, 2019".
 */
export function formatFullDateRange(startIso: string | null, endIso: string | null): string {
  if (!startIso) return '';
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return '';
  if (!endIso) return formatFullDate(startIso);

  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return formatFullDate(startIso);
  if (sameDay(start, end)) return formatFullDate(startIso);

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    return `${MONTHS[start.getMonth()]} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${formatFullDate(startIso)} – ${formatFullDate(endIso)}`;
}

/** "1 memory" / "63 memories" / "1.2K memories" */
export function formatCount(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v >= 10000) return `${(v / 1000).toFixed(0)}K`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
}

export function formatBytes(bytes: number | null | undefined): string {
  const b = bytes ?? 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** "2:31" / "1:02:14" */
export function formatDuration(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function formatFileSizeLabel(mediaCount: number, videoCount = 0): string {
  const photos = Math.max(0, mediaCount - videoCount);
  const parts: string[] = [];
  if (photos > 0) parts.push(`${photos} ${photos === 1 ? 'photo' : 'photos'}`);
  if (videoCount > 0) parts.push(`${videoCount} ${videoCount === 1 ? 'video' : 'videos'}`);
  return parts.length > 0 ? parts.join(' • ') : `${mediaCount} memories`;
}